import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import heroImage1 from "@/assets/hero-hair.webp";
import heroImage2 from "@/assets/hero-hair-2.webp";
import heroImage3 from "@/assets/hero-hair-3.webp";

const slides = [
  {
    image: heroImage1,
    subtitle: "Premium Hair Extensions",
    title: "Plymhair",
    description: "Your Ultimate Hair Destination",
    cta: "Shop Collection",
    link: "/shop",
  },
  {
    image: heroImage2,
    subtitle: "Braids & Twists",
    title: "Plymhair",
    description: "Premium Braids & Protective Styles",
    cta: "Shop Braids",
    link: "/shop?category=Braids",
  },
  {
    image: heroImage3,
    subtitle: "Extensions & Bundles",
    title: "Plymhair",
    description: "Luxury Hair Extensions & Bundles",
    cta: "Shop Extensions",
    link: "/shop?category=Extensions",
  },
];

const HeroSection = () => {
  const [current, setCurrent] = useState(0);
  
  const sectionRef = useRef<HTMLElement>(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);




  const next = useCallback(() => {
    setCurrent((prev) => (prev + 1) % slides.length);
  }, []);

  const prev = useCallback(() => {
    setCurrent((prev) => (prev - 1 + slides.length) % slides.length);
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = e.touches[0].clientX;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(() => {
    const diff = touchStartX.current - touchEndX.current;
    const threshold = 50;
    if (Math.abs(diff) > threshold) {
      if (diff > 0) next();
      else prev();
    }
  }, [next, prev]);

  useEffect(() => {
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next]);

  return (
    <section
      ref={sectionRef}
      className="relative h-[85vh] sm:h-screen min-h-[500px] sm:min-h-[600px] overflow-hidden touch-pan-y"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Slides with Ken Burns zoom */}
      {slides.map((slide, i) => (
        <div
          key={i}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            i === current ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={slide.image}
            alt={slide.description}
            className={`absolute inset-0 w-full h-full object-cover object-top sm:object-center transition-transform duration-[6000ms] ease-out ${
              i === current
                ? i % 2 === 0 ? "scale-110" : "scale-100"
                : i % 2 === 0 ? "scale-100" : "scale-110"
            }`}
            width={1920}
            height={1080}
            {...(i === 0
              ? { fetchPriority: "high" as const, loading: "eager" as const, decoding: "async" as const }
              : { loading: "lazy" as const, decoding: "async" as const })}
          />
          <div className="absolute inset-0 bg-foreground/40" />
        </div>
      ))}

      {/* Single page H1 (visually hidden — visible title rendered per slide as a div) */}
      <h1 className="sr-only">Plymhair — Premium Wigs & Hair Extensions</h1>

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4 sm:px-6 pt-16 sm:pt-0">
        {slides.map((slide, i) => (
          <div
            key={i}
            className={`absolute inset-0 flex flex-col items-center justify-center px-6 pt-16 sm:pt-0 transition-all duration-700 ${
              i === current
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-4"
            }`}
          >
            <span className="font-body text-primary-foreground/70 text-[10px] sm:text-xs tracking-[0.3em] uppercase mb-2 sm:mb-3">
              {slide.subtitle}
            </span>
            <div
              aria-hidden="true"
              className="font-display text-[2.5rem] sm:text-6xl md:text-8xl text-primary-foreground mb-2 sm:mb-4 leading-[1.05]"
            >
              {slide.title}
            </div>
            <p className="font-body text-primary-foreground/85 text-sm sm:text-xl md:text-2xl mb-5 sm:mb-8 max-w-xs sm:max-w-lg">
              {slide.description}
            </p>
            <Link
              to={slide.link}
              className="inline-block bg-primary text-primary-foreground font-body text-[11px] sm:text-sm font-semibold tracking-wider uppercase px-6 sm:px-10 py-2.5 sm:py-4 hover:bg-primary/90 transition-colors"
            >
              {slide.cta}
            </Link>
          </div>
        ))}
      </div>

      {/* Pagination — bottom left, line-style indicators */}
      <div className="absolute bottom-8 sm:bottom-10 left-6 sm:left-10 z-20 flex items-center gap-2">
        <span className="font-body text-primary-foreground/60 text-[10px] tracking-widest uppercase mr-2">
          {String(current + 1).padStart(2, "0")}
        </span>
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            className="group relative h-5 flex items-center"
            aria-label={`Go to slide ${i + 1}`}
          >
            {/* Track */}
            <span className="block w-8 sm:w-10 h-[2px] bg-primary-foreground/25 rounded-full overflow-hidden">
              {/* Fill — animates width when active */}
              <span
                className={`block h-full bg-primary-foreground rounded-full transition-all ease-linear ${
                  i === current
                    ? "w-full duration-[6000ms]"
                    : "w-0 duration-300"
                }`}
              />
            </span>
          </button>
        ))}
        <span className="font-body text-primary-foreground/40 text-[10px] tracking-widest ml-2">
          {String(slides.length).padStart(2, "0")}
        </span>
      </div>
    </section>
  );
};

export default HeroSection;
